import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Column, Project, TicketSummary, User, ViewMode } from '@vd/types';
import { EmptyState, KCard, Menu, MenuDivider, MenuItem, Popover, Switch, Tooltip, cn, useToast } from '@vd/ui';
import { Inbox, AtSign, UserCircle, Users, Search as SearchIcon, Filter, Image as ImageIcon, SlidersHorizontal, Keyboard, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../lib/api';
import { useShortcutMap } from '../lib/keyboard';
import { ShortcutsCheatsheet } from '../features/modals/ShortcutsCheatsheet';
import { useAuth } from '../features/auth/AuthProvider';
import { cardTime } from '../lib/When';
import { copyToClipboard } from '../lib/clipboard';
import { safeOpen } from '../lib/safeOpen';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { AssigneePicker } from '../features/ticket/AssigneePicker';
import { readLocalThumb } from '../features/annotations/localThumbs';
import { BulkActionBar } from '../features/board/BulkActionBar';
import { SingleView } from '../features/board/SingleView';
import { TableView } from '../features/board/TableView';
import { TicketListLoading } from '../features/board/TicketListLoading';
import { ViewToggle } from '../features/board/ViewToggle';
import { FilterModal, type FilterState } from '../features/modals/FilterModal';
import { AdvancedSearchModal } from '../features/modals/AdvancedSearchModal';
import { MoveToProjectModal } from '../features/modals/MoveToProjectModal';
import { EVERY_SCREEN, INBOX_SCREEN } from '../lib/screenNames';
import styles from './InboxPage.module.css';
import { ProjectMark } from '../features/projects/ProjectMark';
import { useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';
import { useOpenCardSession } from '../features/ticket/cardSessionContext';

const INBOX_VIEW_MODES: ReadonlyArray<ViewMode> = ['cards', 'single', 'table'];

const EMPTY_FILTER: FilterState = {
  quickFilters: {
    hasAiActivity: false, capturePartial: false,
    unresolved: false, mentionsMe: false, archived: false,
  },
  assigneeIds: [],
  authorIds: [],
  columnIds: [],
};

function applyInboxFilters(
  tickets: TicketSummary[],
  filter: FilterState,
  query: string,
  currentUserId: string | null,
  /** True when the input `tickets` came from /search/tickets and we
   * should skip the redundant client-side q-match at the bottom. */
  searchActive: boolean,
): TicketSummary[] {
  const q = query.trim().toLowerCase();
  let out = tickets.filter((t) => !t.archivedAt || filter.quickFilters.archived);
  if (filter.quickFilters.hasAiActivity) out = out.filter((t) => t.hasAiActivity);
  if (filter.quickFilters.capturePartial) out = out.filter((t) => t.health !== 'complete');
  if (filter.quickFilters.unresolved) out = out.filter((t) => !t.resolved);
  if (filter.quickFilters.mentionsMe && currentUserId) {
    // Description-level mentions only — comment-level mentions live in
    // the dedicated /me/inbox/mentioned tab. This chip is a quick
    // narrow-by-mention on top of any inbox kind the user is viewing.
    out = out.filter((t) => (t.mentions ?? []).includes(currentUserId));
  }
  if (filter.assigneeIds.length > 0) {
    out = out.filter((t) => t.assignees.some((a) => filter.assigneeIds.includes(a)));
  }
  if (filter.authorIds.length > 0) {
    out = out.filter((t) => filter.authorIds.includes(t.createdBy));
  }
  if (searchActive) {
    // Input list IS the search result; api already matched
    // title/description/comments/url with proper escaping.
  } else if (q) {
    // Sub-2-char fallback: api needs ≥ 2; for shorter queries fall back
    // to client-side over loaded tickets, including the descriptionPreview.
    out = out.filter((t) =>
      t.title.toLowerCase().includes(q) ||
      t.sourceUrl.toLowerCase().includes(q) ||
      (t.descriptionPreview ?? '').toLowerCase().includes(q),
    );
  }
  return out;
}

const titles: Record<string, { label: string; icon: ReactElement; subtitle: string }> = {
  assigned:  { label: 'Assigned to me', icon: <Inbox size={18} />,      subtitle: 'Tickets you’re responsible for across all projects.' },
  created:   { label: 'Created by me',  icon: <UserCircle size={18} />, subtitle: 'Tickets you’ve captured.' },
  mentioned: { label: 'Mentioned',      icon: <AtSign size={18} />,      subtitle: 'Where you’ve been @-mentioned in comments.' },
  team:      { label: 'Team activity',  icon: <Users size={18} />,       subtitle: 'Recent activity across every project you can access.' },
};

type InboxKind = 'assigned' | 'created' | 'mentioned' | 'team';

/**
 * Each project's group on Cards and Table shows its newest tickets first and
 * only this many until View all opens the rest (Jason, 16 Oct, TOADO-864), so
 * the next project is not a long scroll away. The server sends newest first.
 */
export const INBOX_GROUP_SHOWN = 10;

export function InboxPage() {
  const openSession = useOpenCardSession();
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "Inbox"] });
  const { filter = 'assigned' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, currentCompanyId } = useAuth();
  const qc = useQueryClient();
  const meta = titles[filter] ?? titles.assigned!;
  const kind: InboxKind = (['assigned', 'created', 'mentioned', 'team'] as const).includes(filter as InboxKind)
    ? (filter as InboxKind)
    : 'assigned';

  const [overflowTicket, setOverflowTicket] = useState<{ t: TicketSummary; anchor: DOMRect } | null>(null);
  const [assignTicket, setAssignTicket] = useState<{ t: TicketSummary; anchor: DOMRect } | null>(null);
  const [bulkSelection, setBulkSelection] = useState<Set<string>>(new Set());
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkAssignAnchor, setBulkAssignAnchor] = useState<DOMRect | null>(null);
  // Inbox view mode: cards (default, grid of KCards per project group),
  // single (focus reader), or table (high-density per project group).
  // Local state — not persisted in project prefs because this is a
  // cross-project page; future enhancement could persist per-user.
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  // The project groups opened with View all; each page starts with every group showing ten.
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [thumbnails, setThumbnails] = useState(true);
  const [filterState, setFilterState] = useState<FilterState>(EMPTY_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcuts. View modes (1-4) are deliberately wired here
  // even though InboxPage's view-mode set is currently smaller —
  // patchPrefs no-ops cleanly for unsupported modes.
  useShortcutMap({
    '/': (e) => {
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    // `?` is owned by Shell so exactly one shortcuts dialog opens. The
    // toolbar button below still opens this page's detailed cheatsheet.
    Escape: () => {
      if (searchQuery) setSearchQuery('');
    },
  });
  // Single-view mode in inbox uses a project picker (instead of the
  // Cards/Table per-project grouping) so the focus reader stays
  // anchored to one project at a time. Default chosen on first
  // render once tickets resolve.
  const [singleProjectId, setSingleProjectId] = useState<string | null>(null);

  const activeFilterCount = Object.values(filterState.quickFilters).filter(Boolean).length
    + filterState.assigneeIds.length
    + filterState.authorIds.length;

  const toggleBulk = (ticketId: string, next: boolean) => {
    setBulkSelection((prev) => {
      const s = new Set(prev);
      if (next) s.add(ticketId);
      else s.delete(ticketId);
      return s;
    });
  };
  const clearBulk = () => setBulkSelection(new Set());

  // Per-card "in-flight" indicator. Same pattern as the project board:
  // every card-mutating action marks its ticket id(s) so the views
  // render the existing dim+spinner overlay on the card via KCard's
  // `isMoving` prop. Cleared on settle.
  const [movingTicketIds, setMovingTicketIds] = useState<Set<string>>(new Set());
  const markPending = (ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    setMovingTicketIds((prev) => {
      const n = new Set(prev);
      for (const id of arr) n.add(id);
      return n;
    });
  };
  const unmarkPending = (ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    setMovingTicketIds((prev) => {
      const n = new Set(prev);
      for (const id of arr) n.delete(id);
      return n;
    });
  };

  const bulkArchiveMutation = useMutation({
    mutationFn: async (ticketIds: string[]) => {
      markPending(ticketIds);
      const results = await Promise.allSettled(ticketIds.map((id) => api.archiveTicket(id)));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      return { ok, failed: results.length - ok };
    },
    onSettled: (_data, _err, ticketIds) => { unmarkPending(ticketIds); },
    onSuccess: ({ ok, failed }) => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      clearBulk();
      toast.push({
        variant: failed === 0 ? 'success' : 'warning',
        title: failed === 0 ? `Archived ${ok} ticket${ok === 1 ? '' : 's'}` : `Archived ${ok}, ${failed} failed`,
      });
    },
    onError: (err) => toast.push({ variant: 'danger', title: 'Bulk archive failed', description: (err as Error).message }),
  });

  const bulkMoveMutation = useMutation({
    mutationFn: async (vars: { ticketIds: string[]; columnId: string; projectId?: string }) => {
      markPending(vars.ticketIds);
      const results = await Promise.allSettled(
        vars.ticketIds.map((id) => api.moveTicket(id, { columnId: vars.columnId, projectId: vars.projectId })),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      return { ok, failed: results.length - ok };
    },
    onSettled: (_data, _err, vars) => { unmarkPending(vars.ticketIds); },
    onSuccess: ({ ok, failed }) => {
      // Inbox spans all projects — just nuke the entire tickets-by-project
      // cache so every board shows the new truth on next visit.
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      clearBulk();
      setBulkMoveOpen(false);
      toast.push({
        variant: failed === 0 ? 'success' : 'warning',
        title: failed === 0 ? `Moved ${ok} ticket${ok === 1 ? '' : 's'}` : `Moved ${ok}, ${failed} failed`,
      });
    },
    onError: (err) => toast.push({ variant: 'danger', title: 'Bulk move failed', description: (err as Error).message }),
  });

  // Bulk assign / reassign / unassign across the checked tickets. Mode
  // mirrors the AssigneePicker radios:
  //   replace: clear each ticket's assignees, set to the picked set.
  //   add:     append picked to each ticket (no-op if already there).
  //   remove:  delete picked from each ticket's existing list.
  // Fan out per-ticket via Promise.allSettled so a single failed
  // request doesn't poison the rest of the batch — partial successes
  // surface in the toast.
  const bulkAssignMutation = useMutation({
    mutationFn: async (vars: { ticketIds: string[]; userIds: string[]; mode: 'add' | 'replace' | 'remove' }) => {
      markPending(vars.ticketIds);
      const call = (id: string) => vars.mode === 'remove'
        ? api.unassignTicket(id, vars.userIds)
        : api.assignTicket(id, vars.userIds, vars.mode);
      const results = await Promise.allSettled(vars.ticketIds.map(call));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      return { ok, failed: results.length - ok, mode: vars.mode };
    },
    onSettled: (_data, _err, vars) => { unmarkPending(vars.ticketIds); },
    onSuccess: ({ ok, failed, mode }) => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      clearBulk();
      setBulkAssignAnchor(null);
      const verb = mode === 'remove' ? 'Unassigned' : mode === 'add' ? 'Updated' : 'Assigned';
      toast.push({
        variant: failed === 0 ? 'success' : 'warning',
        title: failed === 0
          ? `${verb} ${ok} ticket${ok === 1 ? '' : 's'}`
          : `${verb} ${ok}, ${failed} failed`,
      });
    },
    onError: (err) => toast.push({ variant: 'danger', title: 'Bulk assign failed', description: (err as Error).message }),
  });

  const assignMutation = useMutation({
    mutationFn: ({ ticketId, userIds }: { ticketId: string; userIds: string[] }) =>
      api.assignTicket(ticketId, userIds, 'replace'),
    onMutate: ({ ticketId }) => { markPending(ticketId); },
    onSettled: (_data, _err, vars) => { unmarkPending(vars.ticketId); },
    // Inbox results pivot on `assignees` (assigned-to-me filter) + `commentCount`-
    // proxied mentions, so the easiest correct move is to re-fetch the list we
    // just mutated. Board queries are keyed by project so they auto-refetch
    // on next visit; invalidating them here is cheap insurance.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Assign failed', description: (err as Error).message });
    },
  });

  const ticketsQ = useQuery({
    queryKey: ['inbox', kind, user?.id, currentCompanyId],
    queryFn: async () => {
      if (!user || !currentCompanyId) return { items: [] as TicketSummary[], readAt: null };
      return api.listInbox(kind, currentCompanyId);
    },
    enabled: !!user && !!currentCompanyId,
  });

  // Opening Mentioned or Team activity clears its sidebar number (TOADO-740),
  // from what this page is showing and only up to when the server read it: a
  // load that fails, or one that finishes after the person has left, marks
  // nothing, and something arriving after the read stays counted (Codex
  // review rounds 2 and 3). Each read is marked once.
  const shownReadAt = ticketsQ.data?.readAt ?? null;
  const markedReadAt = useRef<string | null>(null);
  useEffect(() => {
    if ((kind !== 'mentioned' && kind !== 'team') || !currentCompanyId || !shownReadAt) return;
    const mark = `${currentCompanyId}:${kind}:${shownReadAt}`;
    if (markedReadAt.current === mark) return;
    markedReadAt.current = mark;
    void api.markInboxSeen(currentCompanyId, kind, shownReadAt)
      .then(() => qc.invalidateQueries({ queryKey: ['inbox-counts', currentCompanyId] }), () => undefined);
  }, [kind, currentCompanyId, shownReadAt, qc]);

  const projectsQ = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });

  const membersQ = useQuery({
    queryKey: ['members', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });

  const projectsById = useMemo(() => {
    const m = new Map<string, Project>();
    projectsQ.data?.forEach((p) => m.set(p.id, p));
    return m;
  }, [projectsQ.data]);

  const membersById = useMemo(() => {
    const m = new Map<string, User>();
    membersQ.data?.forEach(({ user }) => m.set(user.id, user));
    return m;
  }, [membersQ.data]);

  // Server-side search: when the user types ≥ 2 chars, hit /search/tickets
  // so the match runs against description + comments + URL (not just title
  // and the 240-char descriptionPreview). Inbox is cross-project so we
  // scope to the user's company. Debounce 250 ms so we don't fire one
  // request per keystroke.
  //
  // Same pattern as the board: search results REPLACE `ticketsQ.data`
  // when active, so the api's archive / token-scope filters are the
  // source of truth (the inbox endpoint loads only active tickets, so
  // an id-intersect would silently drop archived hits).
  const debouncedQuery = useDebouncedValue(searchQuery, 250);
  const searchActive = debouncedQuery.trim().length >= 2;
  const searchQ = useQuery({
    queryKey: ['ticket-search', 'inbox', currentCompanyId, debouncedQuery, filterState.quickFilters.archived],
    queryFn: () => api.searchTickets(debouncedQuery, {
      companyId: currentCompanyId ?? undefined,
      includeArchived: filterState.quickFilters.archived,
      limit: 100,
    }),
    enabled: searchActive && !!currentCompanyId,
    staleTime: 30_000,
  });

  // Filter / search applied BEFORE grouping so a search that knocks
  // out every ticket in a project drops the project section entirely.
  const filteredTickets = useMemo(
    () => {
      const source = searchActive ? (searchQ.data?.items ?? []) : (ticketsQ.data?.items ?? []);
      return applyInboxFilters(source, filterState, searchQuery, user?.id ?? null, searchActive);
    },
    [ticketsQ.data, filterState, searchQuery, user?.id, searchActive, searchQ.data],
  );

  useEffect(() => { setOpenGroups(new Set()); }, [kind, currentCompanyId]);
  const toggleGroup = (projectId: string, section: HTMLElement | null) => {
    const opening = !openGroups.has(projectId);
    setOpenGroups((open) => {
      const next = new Set(open);
      if (opening) next.add(projectId); else next.delete(projectId);
      return next;
    });
    // Showing fewer keeps the group's heading in sight, rather than leaving the page far below it.
    if (!opening) section?.scrollIntoView({ block: 'nearest' });
  };

  const grouped = useMemo(() => {
    const groups = new Map<string, TicketSummary[]>();
    filteredTickets.forEach((t) => {
      const arr = groups.get(t.projectId) ?? [];
      arr.push(t);
      groups.set(t.projectId, arr);
    });
    return Array.from(groups.entries());
  }, [filteredTickets]);

  // For Single + Table views we need each project's columns (the
  // status pill / column-name lookup). Cards view doesn't need them
  // at all, so we gate the queries on viewMode to skip a per-project
  // round trip when the user stays on Cards.
  const projectIdsInView = useMemo(() => grouped.map(([id]) => id), [grouped]);
  const columnsQueries = useQueries({
    queries: projectIdsInView.map((id) => ({
      queryKey: ['columns', id],
      queryFn: () => api.listColumns(id),
      enabled: viewMode !== 'cards' && projectIdsInView.length > 0,
      staleTime: 60_000,
    })),
  });
  const columnsByProject = useMemo(() => {
    const m = new Map<string, Column[]>();
    projectIdsInView.forEach((id, i) => {
      const data = columnsQueries[i]?.data;
      if (data) m.set(id, data);
    });
    return m;
    // columnsQueries is a fresh array each render but the .data refs
    // are stable per-project across renders, so depending on the query
    // results array shape is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdsInView, columnsQueries.map((q) => q.data).join(',')]);

  const tickets = ticketsQ.data?.items ?? [];

  // Default the single-view project to the first one with tickets;
  // re-pin if the current pick has been filtered out.
  useEffect(() => {
    if (grouped.length === 0) {
      if (singleProjectId !== null) setSingleProjectId(null);
      return;
    }
    if (!singleProjectId || !grouped.find(([id]) => id === singleProjectId)) {
      setSingleProjectId(grouped[0]![0]);
    }
  }, [grouped, singleProjectId]);
  const singleProjectTickets = singleProjectId
    ? grouped.find(([id]) => id === singleProjectId)?.[1] ?? []
    : [];

  const showThumbsToggle = viewMode === 'cards';

  return (
    // Screens, for pins (TOADO-701): each view is one, at this one address.
    // The header and each project's heading are the same on all of them.
    <div className={styles.page}>
      <header className={styles.header} data-toado-view={EVERY_SCREEN}>
        <div className={styles.headingRow}>
          <div className={styles.iconSlot}>{meta.icon}</div>
          <div className={styles.textCol}>
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{meta.label}</h1>
              <div className={styles.countChip}>
                {tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'}
              </div>
            </div>
            {/* Under the title, not beside it: beside it, it pushed the view
                switch onto a row of its own (t-LA6Kuh). */}
            <p className={styles.subtitle}>{meta.subtitle}</p>
          </div>
          {/* Inline controls. Single view also gets a project picker
              (the focus reader anchors to one project at a time);
              Cards view gets a thumbs toggle (Table doesn't show
              thumbnails). All three views share the search + filter
              controls. They render BEFORE the ViewToggle so the
              toggle stays in the same visual slot across modes. */}
          {viewMode === 'single' && grouped.length > 0 && (
            <select
              className={cn(styles.projectSelect, styles.headerProjectSelect)}
              value={singleProjectId ?? ''}
              onChange={(e) => setSingleProjectId(e.target.value)}
              aria-label="Project"
            >
              {grouped.map(([projectId, items]) => {
                const project = projectsById.get(projectId);
                return (
                  <option key={projectId} value={projectId}>
                    {project?.name ?? 'Project'} ({items.length})
                  </option>
                );
              })}
            </select>
          )}
          {showThumbsToggle && (
            <Tooltip content="Toggle card thumbnails">
              <label className={styles.thumbsToggle}>
                <ImageIcon size={14} />
                <span>Thumbs</span>
                <Switch
                  checked={thumbnails}
                  onChange={(e) => setThumbnails(e.target.checked)}
                />
              </label>
            </Tooltip>
          )}
          <label className={cn(styles.search, styles.headerSearch)}>
            <SearchIcon size={14} />
            <input
              ref={searchInputRef}
              type="search"
              className={styles.searchInput}
              placeholder="Search    /"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchQuery('');
                  e.currentTarget.blur();
                }
              }}
            />
            {searchQuery && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
            <Tooltip content="Keyboard shortcuts (?)" placement="bottom">
              <button
                type="button"
                className={styles.searchAdv}
                onClick={() => setShortcutsOpen(true)}
                aria-label="Keyboard shortcuts"
              >
                <Keyboard size={14} />
              </button>
            </Tooltip>
            <Tooltip content="Advanced search" placement="bottom">
              <button
                type="button"
                className={styles.searchAdv}
                onClick={() => setAdvancedSearchOpen(true)}
                aria-label="Advanced search"
              >
                <SlidersHorizontal size={14} />
              </button>
            </Tooltip>
          </label>
          <button
            type="button"
            className={cn(styles.filterBtn, activeFilterCount > 0 && styles.filterBtnActive)}
            onClick={() => setFilterOpen(true)}
            aria-label="Filter tickets"
          >
            <Filter size={14} />
            Filter
            {activeFilterCount > 0 && <span className={styles.filterCount}>{activeFilterCount}</span>}
          </button>
          <ViewToggle
            viewMode={viewMode}
            onChange={setViewMode}
            modes={INBOX_VIEW_MODES}
          />
          <BulkActionBar
            count={bulkSelection.size}
            onMove={() => setBulkMoveOpen(true)}
            onArchive={() => bulkArchiveMutation.mutate(Array.from(bulkSelection))}
            onAssign={(anchor) => setBulkAssignAnchor(anchor)}
            onClear={clearBulk}
            archiving={bulkArchiveMutation.isPending}
            assigning={bulkAssignMutation.isPending}
          />
        </div>
      </header>

      <div className={cn(styles.scroll, viewMode === 'single' && styles.scrollSingle)} data-toado-view={`${INBOX_SCREEN}/${viewMode}`}>
        {(searchActive ? searchQ.isLoading : ticketsQ.isLoading) ? (
          // Without this branch, the source data is `[]` during the
          // first fetch and the EmptyState below claims "Nothing here
          // yet" — which reads to users as "is this broken?" The
          // ternary picks the right query: searchQ when search is
          // active, ticketsQ for the default inbox view.
          <TicketListLoading label={searchActive ? 'Searching…' : 'Loading…'} />
        ) : filteredTickets.length === 0 ? (
          <div className={styles.emptyWrap}>
            <EmptyState
              icon={meta.icon}
              title={tickets.length === 0 ? 'Nothing here yet' : 'No matching tickets'}
              description={tickets.length === 0
                ? 'Tickets matching this filter will show up here.'
                : 'Adjust the search or filter to see more.'}
            />
          </div>
        ) : viewMode === 'single' ? (
          singleProjectId && singleProjectTickets.length > 0 ? (
            <SingleView
              tickets={singleProjectTickets}
              columns={columnsByProject.get(singleProjectId) ?? []}
              creatorsById={membersById}
              membersById={membersById}
            />
          ) : null
        ) : (
          <div className={styles.groups}>
            {grouped.map(([projectId, items]) => {
              const project = projectsById.get(projectId);
              const open = openGroups.has(projectId);
              const shown = open ? items : items.slice(0, INBOX_GROUP_SHOWN);
              return (
                <section key={projectId} className={styles.group} data-testid="inbox-group">
                  {/* Sticky in Cards and Table views (t-9ihuf1, t-KidLPL); the table's header pins under it. */}
                  <header className={cn(styles.groupHead, styles.groupHeadSticky)} data-toado-view={EVERY_SCREEN}>
                    {project && <ProjectMark project={project} size={20} />}
                    <h2 className={styles.groupTitle}>{project?.name ?? 'Project'}</h2>
                    <span className={styles.groupCount}>{items.length}</span>
                    {/* Once opened, a way back from the top too, not only at the foot of a long list (17 Oct). */}
                    {open && items.length > INBOX_GROUP_SHOWN && (
                      <button type="button" className={styles.groupLink} data-testid="inbox-group-less"
                        onClick={(event) => toggleGroup(projectId, event.currentTarget.closest('section'))}>
                        <ChevronUp size={14} aria-hidden="true" /> Show less
                      </button>
                    )}
                    <button className={styles.groupLink} onClick={() => project && navigate(`/projects/${project.id}/board`)}>
                      Open board →
                    </button>
                  </header>
                  {viewMode === 'cards' && (
                    <div className={styles.grid}>
                      {shown.map((t) => (
                        <KCard
                          key={t.id}
                          ticket={t}
                          creator={membersById.get(t.createdBy)}
                          assignees={t.assignees.map((id) => membersById.get(id)).filter(Boolean) as User[]}
                          showThumbnail={thumbnails}
                          thumbnailUrl={t.thumbnailUrl ?? readLocalThumb(t.id)}
                          formatTime={cardTime}
                          onSessionClick={openSession}
                          onOpen={(ticket) => navigate(`/tickets/${ticket.id}`)}
                          onCopyLink={(ticket) => {
                            void copyToClipboard(`${window.location.origin}/tickets/${ticket.id}`);
                            toast.push({ variant: 'success', title: 'Link copied' });
                          }}
                          onOpenSource={(ticket) => safeOpen(ticket.sourceUrl)}
                          onAssignClick={(ticket, anchor) =>
                            setAssignTicket({ t: ticket, anchor: anchor.getBoundingClientRect() })
                          }
                          onOverflowClick={(ticket, anchor) =>
                            setOverflowTicket({ t: ticket, anchor: anchor.getBoundingClientRect() })
                          }
                          bulkSelectable
                          bulkSelected={bulkSelection.has(t.id)}
                          onBulkToggle={toggleBulk}
                          isMoving={movingTicketIds.has(t.id)}
                        />
                      ))}
                    </div>
                  )}
                  {viewMode === 'table' && (
                    <TableView
                      tickets={shown}
                      columns={columnsByProject.get(projectId) ?? []}
                      creatorsById={membersById}
                      membersById={membersById}
                      embedded
                      onOpenTicket={(t) => navigate(`/tickets/${t.id}`)}
                      onCopyLink={(t) => {
                        void copyToClipboard(`${window.location.origin}/tickets/${t.id}`);
                        toast.push({ variant: 'success', title: 'Link copied' });
                      }}
                      onOpenSource={(t) => safeOpen(t.sourceUrl)}
                      onAssignClick={(t, anchor) =>
                        setAssignTicket({ t, anchor: anchor.getBoundingClientRect() })
                      }
                      onOverflowClick={(t, anchor) =>
                        setOverflowTicket({ t, anchor: anchor.getBoundingClientRect() })
                      }
                      bulkSelection={bulkSelection}
                      onBulkToggle={toggleBulk}
                      movingTicketIds={movingTicketIds}
                    />
                  )}
                  {/* Single view is rendered above this loop with its
                      own project picker, so we skip it inside the
                      per-group section. */}
                  {items.length > INBOX_GROUP_SHOWN && (
                    <button
                      type="button"
                      className={styles.groupMore}
                      aria-expanded={open}
                      onClick={(event) => toggleGroup(projectId, event.currentTarget.closest('section'))}
                    >
                      {open
                        ? <><ChevronUp size={14} aria-hidden="true" /> Show the newest {INBOX_GROUP_SHOWN}</>
                        : <><ChevronDown size={14} aria-hidden="true" /> View all {items.length}</>}
                    </button>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <Popover
        open={!!overflowTicket}
        onClose={() => setOverflowTicket(null)}
        anchor={overflowTicket?.anchor ?? null}
        placement="bottom-end"
      >
        <Menu>
          <MenuItem onClick={() => { if (overflowTicket) navigate(`/tickets/${overflowTicket.t.id}`); setOverflowTicket(null); }}>
            Open ticket
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (overflowTicket) {
                void copyToClipboard(`${window.location.origin}/tickets/${overflowTicket.t.id}`);
                toast.push({ variant: 'success', title: 'Link copied' });
              }
              setOverflowTicket(null);
            }}
          >
            Copy link
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (overflowTicket) navigate(`/projects/${overflowTicket.t.projectId}/board`);
              setOverflowTicket(null);
            }}
          >
            Go to project
          </MenuItem>
          <MenuDivider />
          <MenuItem
            onClick={() => {
              toast.push({ variant: 'success', title: 'Archived', description: overflowTicket?.t.title.slice(0, 60) });
              setOverflowTicket(null);
            }}
          >
            Archive
          </MenuItem>
        </Menu>
      </Popover>

      <Popover
        open={!!assignTicket}
        onClose={() => setAssignTicket(null)}
        anchor={assignTicket?.anchor ?? null}
        placement="bottom-end"
        width={280}
      >
        {assignTicket && (
          <AssigneePicker
            allMembers={membersQ.data?.map((m) => m.user) ?? []}
            selected={assignTicket.t.assignees}
            onDone={(userIds) => {
              assignMutation.mutate({ ticketId: assignTicket.t.id, userIds });
              setAssignTicket(null);
            }}
            onCancel={() => setAssignTicket(null)}
          />
        )}
      </Popover>

      {/* Bulk assign / reassign / unassign — shown when one or more
          tickets are selected and the user clicks "Assign…" in the
          bulk action bar. The picker starts with no checkmarks because
          each ticket may have a different assignee set; the chosen
          mode (replace / add / remove) decides how the picked user
          ids apply across every selected ticket. */}
      <Popover
        open={!!bulkAssignAnchor}
        onClose={() => setBulkAssignAnchor(null)}
        anchor={bulkAssignAnchor}
        placement="bottom-end"
        width={300}
      >
        {bulkAssignAnchor && (
          <AssigneePicker
            allMembers={membersQ.data?.map((m) => m.user) ?? []}
            selected={[]}
            bulkMode
            onDone={(userIds, mode) => {
              bulkAssignMutation.mutate({
                ticketIds: Array.from(bulkSelection),
                userIds,
                mode,
              });
            }}
            onCancel={() => setBulkAssignAnchor(null)}
          />
        )}
      </Popover>

      {/* Bulk move — tickets could span multiple projects in the inbox view,
          so there's no "current project" to anchor the modal. We pass an
          empty string; the modal still lists every project as a target. */}
      <MoveToProjectModal
        open={bulkMoveOpen}
        onClose={() => setBulkMoveOpen(false)}
        currentProjectId=""
        onMove={(targetProjectId, targetColumnId) => {
          bulkMoveMutation.mutate({
            ticketIds: Array.from(bulkSelection),
            columnId: targetColumnId,
            projectId: targetProjectId,
          });
        }}
      />

      {/* Inbox-scope filter modal. Pass no columns since the inbox
          spans projects (each ticket has its own project's columns)
          — the column filter section auto-hides when columns is
          empty. Quick filters / assignee / author still apply. */}
      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filterState}
        onApply={(next) => setFilterState(next)}
      />

      <AdvancedSearchModal
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
      />

      <ShortcutsCheatsheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
